import {RouterProvider} from '@wandb/components';
import {ToastContainer} from '@wandb/weave/common/components/elements/Toast';
import {OptionalTraceServerClientContextProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import {WeaveFeaturesContextProvider} from '@wandb/weave/context';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';

import AddEmailAlert from '../components/AddEmailAlert';
import {GlobalBannerContextProvider} from '../components/GlobalBanners/GlobalBannerContextProvider';
import MessageOfTheDay from '../components/MessageOfTheDay';
import NavContextProvider from '../components/NavContextProvider';
import {RunQueuePopoverProvider} from '../components/RunQueueItemsPopover';
import {AppSearch} from '../components/Search';
import SearchNav from '../components/Search/SearchNav';
import {AccountSelectorContextProviderWrapper} from '../components/Search/SearchNav/AccountSelectorContextProviderWrapper';
import {ComputeGraphContextProvider} from '../ComputeGraphContextProvider';
import {backendTraceBaseUrl} from '../config';
import {AppLoaderContextProvider, LoaderProxy} from '../containers/AppLoader';
import {useSyncedPollingPublisher} from '../state/polling/hooks';
import {useInitViewer} from '../state/viewer/hooks';
import {useSetAccessTokenFromCookieFromQS} from '../util/auth';
import {useConsentAwareAnalyticsInjection} from '../util/consentAwareAnalyticSetup';
import {setGlobalHistory, useUpdateCanonicalURL} from '../util/history';
import {useHref, useNavigate} from '../util/react-router-v6-shim';
import {StarredProjectsContextProvider} from '../util/StarredProjectsContextProvider';
import {ColorModeProvider} from '../util/useColorMode';
import {useWeaveFeatures} from '../util/useWeaveFeatures';
import {App, MeticulousWrapper} from './../App';
import {AppBanners} from './AppBanners';
import {AppRoutes} from './AppRoutes';

export const AppWithRoutes = () => {
  const navigate = useNavigate();

  useConsentAwareAnalyticsInjection();
  useUpdateCanonicalURL();
  useSetAccessTokenFromCookieFromQS();

  // This is a temporary workaround to allow us to set the global history
  // and avoid creating history b/c the preferred approach is to use the
  // BrowserRouter component and capture history using the useHistory hook.
  // However, we are using the global history in redux and it is a much larger
  // effort to fix that. Sorry :(
  const history = useHistory();
  useEffect(() => {
    setGlobalHistory(history);
  }, [history]);

  // This starts the global polling manager that allows any consumers
  // to subscribe and poll in sync with other consumers
  useSyncedPollingPublisher();

  const weaveFeatures = useWeaveFeatures();

  // We keep the viewer state in the redux store, you can't call Auth.loggedIn
  // until this has been loaded.
  useInitViewer();

  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      <WeaveFeaturesContextProvider features={weaveFeatures}>
        <ComputeGraphContextProvider>
          <OptionalTraceServerClientContextProvider
            baseUrl={backendTraceBaseUrl()}>
            <AccountSelectorContextProviderWrapper>
              <StarredProjectsContextProvider>
                <AppLoaderContextProvider>
                  {/* This requires account selector access, so it must be mounted BELOW the account selector context provider */}
                  <MeticulousWrapper>
                    <ColorModeProvider>
                      <App>
                        <GlobalBannerContextProvider>
                          <RunQueuePopoverProvider>
                            <NavContextProvider>
                              <SearchNav />
                              <AppSearch />
                              <MessageOfTheDay />
                              <AppBanners />
                              <AddEmailAlert />
                              <div className="main">
                                <ToastContainer />
                                <React.Suspense
                                  fallback={
                                    <LoaderProxy name="fallback-loader" />
                                  }>
                                  <AppRoutes />
                                </React.Suspense>
                              </div>
                            </NavContextProvider>
                          </RunQueuePopoverProvider>
                        </GlobalBannerContextProvider>
                      </App>
                    </ColorModeProvider>
                  </MeticulousWrapper>
                </AppLoaderContextProvider>
              </StarredProjectsContextProvider>
            </AccountSelectorContextProviderWrapper>
          </OptionalTraceServerClientContextProvider>
        </ComputeGraphContextProvider>
      </WeaveFeaturesContextProvider>
    </RouterProvider>
  );
};
